/**
 * Motor estadístico para el Informe Ejecutivo NOM-035 (cruces y promedios globales).
 */

import type { Employee, Nom35Assessment } from '@/types';
import {
  computeCategoryScoresFromAnswers,
  scoreNom35G1,
  type Nom35RiskLevel,
} from '@/lib/nom35';

/** Etiquetas de rango de edad alineadas a criterios de reporte (STPS / tabulación). */
export const NOM35_STPS_AGE_RANGE_LABELS = [
  '15-19',
  '20-24',
  '25-29',
  '30-34',
  '35-39',
  '40-44',
  '45-49',
  '50-54',
  '55-59',
  '60-64',
  '65 o más',
  'No especificado',
] as const;

/** Umbrales de puntaje total por guía (misma lógica que en nom35.ts; no exportada allí). */
const RISK_THRESHOLDS_G2: { max: number; level: Nom35RiskLevel }[] = [
  { max: 46, level: 'Nulo' },
  { max: 83, level: 'Bajo' },
  { max: 120, level: 'Medio' },
  { max: 156, level: 'Alto' },
  { max: Infinity, level: 'Muy Alto' },
];

const RISK_THRESHOLDS_G3: { max: number; level: Nom35RiskLevel }[] = [
  { max: 43, level: 'Nulo' },
  { max: 72, level: 'Bajo' },
  { max: 101, level: 'Medio' },
  { max: 115, level: 'Alto' },
  { max: Infinity, level: 'Muy Alto' },
];

function getRiskLevelFromTotalScore(score: number, guideType: 'NOM35_G2' | 'NOM35_G3'): Nom35RiskLevel {
  const thresholds = guideType === 'NOM35_G2' ? RISK_THRESHOLDS_G2 : RISK_THRESHOLDS_G3;
  for (const t of thresholds) {
    if (score <= t.max) return t.level;
  }
  return 'Muy Alto';
}

function parseBirthDateParts(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

/**
 * Calcula la edad a partir de fecha de nacimiento (YYYY-MM-DD) y devuelve la etiqueta de rango STPS.
 */
export function getNom35AgeRange(birthDate?: string): string {
  if (!birthDate?.trim()) return 'No especificado';
  const parts = parseBirthDateParts(birthDate);
  if (!parts) return 'No especificado';

  const birth = new Date(parts.y, parts.m - 1, parts.d);
  if (Number.isNaN(birth.getTime())) return 'No especificado';

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  if (!Number.isFinite(age) || age < 15) return 'No especificado';
  if (age <= 19) return '15-19';
  if (age <= 24) return '20-24';
  if (age <= 29) return '25-29';
  if (age <= 34) return '30-34';
  if (age <= 39) return '35-39';
  if (age <= 44) return '40-44';
  if (age <= 49) return '45-49';
  if (age <= 54) return '50-54';
  if (age <= 59) return '55-59';
  if (age <= 64) return '60-64';
  return '65 o más';
}

/** Conteos por nivel de riesgo para una fila de tabla de frecuencias (cruce demográfico G2/G3). */
export interface Nom35RiskFrequencyCounts {
  nulo: number;
  bajo: number;
  medio: number;
  alto: number;
  muyAlto: number;
  total: number;
}

export type Nom35RiskFrequencyTable = Record<string, Nom35RiskFrequencyCounts>;

function emptyRiskCounts(): Nom35RiskFrequencyCounts {
  return { nulo: 0, bajo: 0, medio: 0, alto: 0, muyAlto: 0, total: 0 };
}

function incrementRiskCount(table: Nom35RiskFrequencyTable, key: string, level: Nom35RiskLevel): void {
  if (!table[key]) table[key] = emptyRiskCounts();
  const row = table[key];
  switch (level) {
    case 'Nulo':
      row.nulo += 1;
      break;
    case 'Bajo':
      row.bajo += 1;
      break;
    case 'Medio':
      row.medio += 1;
      break;
    case 'Alto':
      row.alto += 1;
      break;
    case 'Muy Alto':
      row.muyAlto += 1;
      break;
    default:
      break;
  }
  row.total += 1;
}

const SEX_TABLE_KEYS = ['Masculino', 'Femenino', 'Otro', 'No especificado'] as const;

function normalizeSexKey(sex: Employee['sex']): (typeof SEX_TABLE_KEYS)[number] {
  if (sex === 'Masculino' || sex === 'Femenino' || sex === 'Otro') return sex;
  return 'No especificado';
}

/** Promedios globales del centro (puntaje total y medias por categoría y dominio) — Guía II/III. */
export interface Nom35ExecutiveGlobalAverages {
  averageTotal: number;
  averageByCategory: Record<string, number>;
  averageByDomain: Record<string, number>;
}

export interface Nom35ExecutiveDemographicCrosses {
  riskBySex: Nom35RiskFrequencyTable;
  riskByAgeRange: Nom35RiskFrequencyTable;
  riskByDepartment: Nom35RiskFrequencyTable;
}

/** Bloque ejecutivo Guía II o III (Likert). */
export interface Nom35ExecutiveG2G3Result {
  guideType: 'NOM35_G2' | 'NOM35_G3';
  validAssessmentCount: number;
  globalAverages: Nom35ExecutiveGlobalAverages;
  globalRiskLevel: Nom35RiskLevel;
  crosses: Nom35ExecutiveDemographicCrosses;
}

/** Conteos Guía I por dimensión demográfica (valoración clínica). */
export interface Nom35G1AttentionFrequencyCounts {
  requiereAtencion: number;
  noRequiere: number;
  total: number;
}

export type Nom35G1AttentionFrequencyTable = Record<string, Nom35G1AttentionFrequencyCounts>;

function emptyG1Counts(): Nom35G1AttentionFrequencyCounts {
  return { requiereAtencion: 0, noRequiere: 0, total: 0 };
}

function incrementG1Attention(table: Nom35G1AttentionFrequencyTable, key: string, requiresAttention: boolean): void {
  if (!table[key]) table[key] = emptyG1Counts();
  const row = table[key];
  if (requiresAttention) row.requiereAtencion += 1;
  else row.noRequiere += 1;
  row.total += 1;
}

function assessmentG1RequiresClinicalAttention(a: Nom35Assessment): boolean {
  if (a.requiresClinicalAttention === true) return true;
  if (a.requiresClinicalAttention === false) return false;
  if (a.answers && Object.keys(a.answers).length > 0) {
    return scoreNom35G1(a.answers).requiresClinicalAttention;
  }
  return false;
}

/** Bloque ejecutivo Guía I (Acontecimientos Traumáticos Severos). */
export interface Nom35ExecutiveG1Block {
  totalEvaluated: number;
  requiresAttentionCount: number;
  crosses: {
    bySex: Nom35G1AttentionFrequencyTable;
    byAgeRange: Nom35G1AttentionFrequencyTable;
    byDepartment: Nom35G1AttentionFrequencyTable;
  };
}

/**
 * Resultado completo del informe ejecutivo: Guía I y/o Guía II/III.
 * Cada bloque es null si no hubo evaluaciones completadas válidas para esa guía.
 */
export interface Nom35ExecutiveGlobalResult {
  g1: Nom35ExecutiveG1Block | null;
  g2g3: Nom35ExecutiveG2G3Result | null;
}

function resolveG2G3GuideType(assessments: Nom35Assessment[]): 'NOM35_G2' | 'NOM35_G3' | null {
  const hasAnswers = (a: Nom35Assessment) => Boolean(a.answers && Object.keys(a.answers).length > 0);
  if (assessments.some((a) => a.guideType === 'NOM35_G3' && hasAnswers(a))) return 'NOM35_G3';
  if (assessments.some((a) => a.guideType === 'NOM35_G2' && hasAnswers(a))) return 'NOM35_G2';
  return null;
}

function computeG2G3Block(
  assessments: Nom35Assessment[],
  employees: Employee[],
  guideType: 'NOM35_G2' | 'NOM35_G3'
): Nom35ExecutiveG2G3Result | null {
  const employeeById = new Map(employees.map((e) => [e.id, e]));

  const joined: { assessment: Nom35Assessment; employee: Employee }[] = [];
  for (const a of assessments) {
    if (a.status !== 'completed' || a.guideType !== guideType) continue;
    if (!a.answers || Object.keys(a.answers).length === 0) continue;
    const emp = employeeById.get(a.employeeId);
    if (!emp) continue;
    joined.push({ assessment: a, employee: emp });
  }

  const n = joined.length;
  if (n === 0) return null;

  const sumTotal = { value: 0 };
  const sumByCategory: Record<string, number> = {};
  const sumByDomain: Record<string, number> = {};

  const riskBySex: Nom35RiskFrequencyTable = Object.fromEntries(
    SEX_TABLE_KEYS.map((k) => [k, emptyRiskCounts()])
  ) as Nom35RiskFrequencyTable;

  const riskByAgeRange: Nom35RiskFrequencyTable = {};
  const riskByDepartment: Nom35RiskFrequencyTable = {};

  for (const { assessment, employee } of joined) {
    const { result } = computeCategoryScoresFromAnswers(assessment.answers!, guideType);

    sumTotal.value += result.total;
    for (const [name, score] of Object.entries(result.byCategory)) {
      sumByCategory[name] = (sumByCategory[name] ?? 0) + score;
    }
    for (const [name, score] of Object.entries(result.byDomain)) {
      sumByDomain[name] = (sumByDomain[name] ?? 0) + score;
    }

    const individualLevel = result.riskLevel;
    incrementRiskCount(riskBySex, normalizeSexKey(employee.sex), individualLevel);
    incrementRiskCount(riskByAgeRange, getNom35AgeRange(employee.birthDate), individualLevel);
    const deptKey = employee.department?.trim() || 'No especificado';
    incrementRiskCount(riskByDepartment, deptKey, individualLevel);
  }

  const averageTotal = sumTotal.value / n;
  const averageByCategory: Record<string, number> = {};
  for (const [k, sum] of Object.entries(sumByCategory)) {
    averageByCategory[k] = sum / n;
  }
  const averageByDomain: Record<string, number> = {};
  for (const [k, sum] of Object.entries(sumByDomain)) {
    averageByDomain[k] = sum / n;
  }

  const globalRiskLevel = getRiskLevelFromTotalScore(averageTotal, guideType);

  return {
    guideType,
    validAssessmentCount: n,
    globalAverages: {
      averageTotal,
      averageByCategory,
      averageByDomain,
    },
    globalRiskLevel,
    crosses: {
      riskBySex,
      riskByAgeRange,
      riskByDepartment,
    },
  };
}

function computeG1Block(assessments: Nom35Assessment[], employees: Employee[]): Nom35ExecutiveG1Block | null {
  const employeeById = new Map(employees.map((e) => [e.id, e]));

  const joined: { assessment: Nom35Assessment; employee: Employee }[] = [];
  for (const a of assessments) {
    if (a.status !== 'completed' || a.guideType !== 'NOM35_G1') continue;
    if (!a.answers || Object.keys(a.answers).length === 0) continue;
    const emp = employeeById.get(a.employeeId);
    if (!emp) continue;
    joined.push({ assessment: a, employee: emp });
  }

  const n = joined.length;
  if (n === 0) return null;

  let requiresAttentionCount = 0;
  const bySex: Nom35G1AttentionFrequencyTable = Object.fromEntries(
    SEX_TABLE_KEYS.map((k) => [k, emptyG1Counts()])
  ) as Nom35G1AttentionFrequencyTable;
  const byAgeRange: Nom35G1AttentionFrequencyTable = {};
  const byDepartment: Nom35G1AttentionFrequencyTable = {};

  for (const { assessment, employee } of joined) {
    const requires = assessmentG1RequiresClinicalAttention(assessment);
    if (requires) requiresAttentionCount += 1;

    incrementG1Attention(bySex, normalizeSexKey(employee.sex), requires);
    incrementG1Attention(byAgeRange, getNom35AgeRange(employee.birthDate), requires);
    const deptKey = employee.department?.trim() || 'No especificado';
    incrementG1Attention(byDepartment, deptKey, requires);
  }

  return {
    totalEvaluated: n,
    requiresAttentionCount,
    crosses: {
      bySex,
      byAgeRange,
      byDepartment,
    },
  };
}

/**
 * Cruza evaluaciones NOM-035 completadas con empleados: Guía I (ATS) y Guía II/III (Likert).
 */
export function computeGlobalExecutiveNom35(
  assessments: Nom35Assessment[],
  employees: Employee[]
): Nom35ExecutiveGlobalResult {
  const g2g3Guide = resolveG2G3GuideType(assessments);
  const g2g3 = g2g3Guide ? computeG2G3Block(assessments, employees, g2g3Guide) : null;
  const g1 = computeG1Block(assessments, employees);

  return { g1, g2g3 };
}
